<template>
  <div class="photo-wall-page">
    <Loader v-if="isLoading" style="position: absolute"></Loader>

    <div v-else class="ui form">
      <!--<router-link :to="{name: 'CommunityAdminDashboard'}" lang="en" hreflang="en" class="ui small basic button right floated">Back to Dashboard</router-link>-->
      <h1 style="margin-right: 25px">Add a Photo Wall to your Website!</h1>

      <!-- Description section -->
      <div class="description-section photo-wall-page__description">
        <VideoIntroSnippet class="description-section__video" :thumbnail="`${contentBaseUri}/images/misc/photowall-intro.jpg`" :videoUrl="`${contentBaseUri}/videos/howto/photowall-intro.mp4`" />

        <div class="description-section__text">
          Share stunning visuals right on your website that link back to your destination's adventures!<br />
          Inspire travelers to plan their next trip with our easy Photo Wall integration.<br />
        </div>
      </div>
      <!-- / Description section -->

      <div class="ui stackable grid">
        <div class="ten wide column">
          <table class="ui very basic table">
            <thead>
              <tr>
                <th>
                  <label class="cms-section-title" style="margin-top: 1em !important"> Photo Walls </label>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="photoWidget in photoWidgets" :key="photoWidget.widgetId">
                <td>
                  <router-link :to="{ name: 'PhotoWallAdmin', params: { pageId: pageId, photoWallId: photoWidget.widgetId } }">
                    <LinkWithIcon class="adventure-wall-link">
                      <IconEmbedded name="edit-pencil_2" :size="21" />
                      <span>
                        <b>{{ photoWidget.name }}</b>
                        <span style="margin-left: 3px">({{ photoWidget.photoCount }} photos)</span>
                      </span>
                    </LinkWithIcon>
                  </router-link>
                  <div v-if="photoWidget.lastHitTime" class="wall-usage">
                    <button
                      type="button"
                      class="wall-usage__toggle"
                      :class="{ 'wall-usage__toggle--expandable': getRecentSources(photoWidget).length }"
                      :disabled="!getRecentSources(photoWidget).length"
                      @click="toggleUsage(photoWidget.widgetId)"
                    >
                      <span>Last used {{ formatTimeAgo(photoWidget.lastHitTime) }}</span>
                      <IconEmbedded
                        v-if="getRecentSources(photoWidget).length"
                        class="wall-usage__caret"
                        :name="isUsageExpanded(photoWidget.widgetId) ? 'caret-top_3' : 'caret-bottom_3'"
                        :size="12"
                        color="rgba(0, 0, 0, 0.45)"
                      />
                    </button>
                    <ul v-if="isUsageExpanded(photoWidget.widgetId) && getRecentSources(photoWidget).length" class="wall-usage__sources">
                      <li v-for="source in getRecentSources(photoWidget)" :key="source.source">
                        <a :href="source.source" target="_blank" rel="noopener">{{ source.source }}</a>
                      </li>
                    </ul>
                  </div>
                </td>
                <td>
                  <button class="ui tiny basic primary icon button" style="margin-right: 8px" @click="copy(photoWidget.widgetId)">
                    <i class="icon copy outline"></i>
                    Copy Code
                  </button>
                  <router-link :to="{ name: 'PhotoWallAdmin', params: { pageId: pageId, photoWallId: photoWidget.widgetId } }" class="ui tiny basic button"> Edit </router-link>
                  <span
                    class="photo-wall-page__delete-icon"
                    role="button"
                    tabindex="0"
                    aria-label="Delete photo wall"
                    @click="askToDelete(photoWidget.widgetId)"
                    @keydown.enter.prevent="askToDelete(photoWidget.widgetId)"
                    @keydown.space.prevent="askToDelete(photoWidget.widgetId)"
                  >
                    <IconEmbedded name="trashcan-alt_2" :size="16" color="rgba(0, 0, 0, 0.45)" />
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="justCopied" style="float: right"><b>Code Copied to Clipboard</b></div>
          <div style="margin-top: 8px">
            <SrpButton size="small" :color="photoWidgets?.length > 0 ? 'teal' : 'orange'" @click="createNew" :isDisabled="isCreatingNewGroup">{{
              photoWidgets?.length > 0 ? "New Photo Wall" : "Get Started"
            }}</SrpButton>
          </div>
        </div>
        <div class="six wide column">
          <img class="ui small image photo-wall-img" style="width: 100%" src="https://cdn.shrpa.com/images/misc/integrations/PhotoWall-med.jpg" />
        </div>
      </div>

      <label class="cms-section-title" style="margin-top: 50px !important">Other Settings</label>
      <p>
        <span v-if="integrationSettings" class="ui toggle checkbox">
          <input type="checkbox" :disabled="isSaving" v-model="integrationSettings.openAdventuresInNewTab" @change="otherSettingsChanged" />
          <label>Open adventures in a new browser tab/window.</label>
        </span>
      </p>
      <p v-if="justSaved">
        <b>Settings Saved!</b>
      </p>

      <label class="cms-section-title" style="margin-top: 1em !important">What do I do with that code?</label>
      <p>
        Add it to your website (Wordpress, Simpleview, etc...)
        <br />
        Here's a full walkthrough!
        <br />
        <a style="text-decoration: underline" target="_blank" href="https://explore.shrpa.com/integrations/how-to-integrate-adventures-on-your-site/"
          >https://explore.shrpa.com/integrations/how-to-integrate-adventures-on-your-site/</a
        >
      </p>

      <br />
      <label class="cms-section-title">Questions?</label>
      <p>
        Having issues integrating this into your site? If you or your developer have any questions, we're happy to help! <br />
        Email us anytime at <CopyText />
      </p>
    </div>

    <!--Delete Modal-->
    <SrpModal v-model:isVisible="showDeleteModal" size="small" :isClosable="false">
      <template #header><h2 class="global-h2">Delete Photo Wall</h2></template>
      <template #content>
        Are you sure you want to delete this photo wall?<br />
        This action cannot be undone.
      </template>
      <template #footer>
        <div class="ui basic black button" @click="cancelDelete()">Cancel</div>
        <SrpButton color="red" :isDisabled="isDeletingList" @click="deleteList()">Delete</SrpButton>
        <span v-if="isDeletingList"><b>Deleting...</b></span>
      </template>
    </SrpModal>
  </div>
</template>

<script lang="ts">
import axios from "axios";
import { defineComponent } from "vue";
import OrgContext from "@logic/OrgContext";
import AdminContext from "@logic/AdminContext";

// Types
import { PartnerNavInfo } from "@contracts/partnerNavInfo";
import { PhotoWidgetSummary } from "@contracts/photoWidget";
import { WidgetUsageSourceSummary } from "@contracts/adventureWall";

// Components
import CopyText from "@components/CopyText.vue";
import Loader from "@components/Loader/Loader.vue";
import VideoIntroSnippet from "@components/VideoIntroSnippet.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import { useHead } from "@unhead/vue";
import { MetricSender } from "@/helpers/MetricSender";
import DateUtils from "@logic/DateUtils";

// Photo Walls page
export default defineComponent({
  name: "WidgetDirections",

  components: {
    VideoIntroSnippet,
    SrpButton,
    SrpModal,
    Loader,
    CopyText,
    LinkWithIcon,
    IconEmbedded,
  },

  data() {
    return {
      title: "Photo Walls",
      pageId: "",
      orgInContext: null as PartnerNavInfo,
      photoWidgets: null as PhotoWidgetSummary[] | null,
      integrationSettings: null,
      code: "",
      justCopied: false,
      isSaving: false,
      justSaved: false,
      isLoading: true,
      // @ts-ignore
      contentBaseUri: globalThis.Bootstrap.Config.contentCdnBaseUri,

      isCreatingNewGroup: false,
      expandedUsageIds: {} as Record<string, boolean>,
      showDeleteModal: false,
      listIdToDelete: null as string | null,
      isDeletingList: false,
    };
  },

  computed: {},

  created() {
    this.globalRoot.widgetDirectionsComponent = this;
  },

  async mounted() {
    useHead({ title: () => this.title ?? "" });

    // @ts-ignore
    this.pageId = this.$route.params.pageId;
    await AdminContext.setOrgInContextFromCustomerId(this.pageId);
    this.orgInContext = OrgContext.getOrgInContext(this);
    this.title += " - " + this.orgInContext?.name;
    await this.loadData();

    MetricSender.sendMetric("Cust-ViewPhotoWalls", null);
  },

  methods: {
    formatTimeAgo: DateUtils.formatTimeAgo,

    async loadData() {
      this.isLoading = true;

      const photoWidgetsResponse = await axios.get<PhotoWidgetSummary[]>(`${import.meta.env.VITE_API_URL}/photo-widget-admin/${this.pageId}/widgets/summary`);
      this.photoWidgets = photoWidgetsResponse.data;

      const settingsResponse = await axios.get(`${import.meta.env.VITE_API_URL}/widget-admin/${this.pageId}/widgets/settings`);
      this.integrationSettings = settingsResponse.data.widgetSettings;

      this.isLoading = false;
    },
    toggleUsage(widgetId: string) {
      this.expandedUsageIds = {
        ...this.expandedUsageIds,
        [widgetId]: !this.expandedUsageIds[widgetId],
      };
    },
    getRecentSources(widget: PhotoWidgetSummary): WidgetUsageSourceSummary[] {
      return (widget.recentSources ?? []).filter(s => {
        const source = s.source?.trim();
        return source && source.toLowerCase() !== "<missing>";
      });
    },
    isUsageExpanded(widgetId: string) {
      return !!this.expandedUsageIds[widgetId];
    },
    async otherSettingsChanged(event, value: boolean) {
      this.isSaving = true;
      await axios.put(`${import.meta.env.VITE_API_URL}/widget-admin/${this.pageId}/widgets/settings?openInNewWindow=${this.integrationSettings.openAdventuresInNewTab}`);
      this.justSaved = true;
      this.isSaving = false;
      setTimeout(() => (this.justSaved = false), 5000);
    },
    async copy(groupId) {
      const code = `<script id="shrpa-go__${this.pageId}--${groupId}" src="https://shrpa.com/widget/photos.js" async></` + `script>`;
      await navigator.clipboard.writeText(code);

      this.justCopied = true;
      setTimeout(() => {
        this.justCopied = false;
      }, 5000);
    },
    viewCommunityPage() {
      this.$router.push({ name: "Pages", params: { pageId: this.orgInContext.uniqueName } });
    },
    async createNew() {
      if (this.isCreatingNewGroup) return;

      this.isCreatingNewGroup = true;
      const name = "Photo Wall " + (this.photoWidgets?.length + 1);
      const config = { headers: { "Content-Type": "application/json" } };
      const newPhotoWidget = await axios.post(`${import.meta.env.VITE_API_URL}/photo-widget-admin/${this.pageId}/widgets`, JSON.stringify(name), config);
      this.$router.push({ name: "PhotoWallAdmin", params: { pageId: this.pageId, photoWallId: newPhotoWidget.data.widgetId } });
    },
    askToDelete(listIdToDelete: string) {
      this.listIdToDelete = listIdToDelete;
      this.showDeleteModal = true;
    },
    cancelDelete() {
      this.listIdToDelete = null;
      this.showDeleteModal = false;
    },
    async deleteList() {
      if (!this.listIdToDelete || !this.photoWidgets) return;

      this.isDeletingList = true;
      await axios.delete(`${import.meta.env.VITE_API_URL}/photo-widget-admin/${this.pageId}/widgets/${this.listIdToDelete}`);
      this.photoWidgets.splice(
        this.photoWidgets.findIndex(g => g.widgetId === this.listIdToDelete),
        1
      );
      this.listIdToDelete = null;
      this.isDeletingList = false;
      this.showDeleteModal = false;
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

.adventure-wall-link {
  & > :deep(span),
  & > :deep(span > span) {
    text-decoration: none;
  }

  & > :deep(span b) {
    text-decoration: underline;
    text-decoration-style: dashed;
    text-decoration-color: rgba(0, 0, 0, 0.3);
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
  }

  &:hover > :deep(span b) {
    text-decoration: none;
  }
}

.wall-usage {
  margin-top: 4px;
  margin-left: 29px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);

  &__toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: default;

    &--expandable {
      cursor: pointer;

      &:hover {
        color: rgba(0, 0, 0, 0.75);
      }
    }
  }

  &__caret {
    flex-shrink: 0;
  }

  &__sources {
    margin: 6px 0 0;
    padding-left: 16px;
    list-style: disc;

    li {
      margin-bottom: 2px;
      word-break: break-all;
    }

    a {
      color: inherit;
      text-decoration: underline;
    }
  }
}

.photo-wall-img {
  margin-top: 60px;
  @media screen and (max-width: 420px) {
    margin-top: 0px;
  }
}

.limit-hit-message {
  margin-top: 10px;
}

// Description section ========================================================
.description-section {
  display: inline-flex;

  &__video {
    margin: 5px 22px 10px 0;
  }

  &__text {
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .description-section {
    display: inline-block;

    &__video {
      float: left;
      position: relative;
    }
  }
}

// Photo wall page ============================================================
.photo-wall-page {
  &__description {
    width: 100%;
    margin-bottom: 15px;
  }

  &__delete-icon {
    display: inline-flex;
    align-items: center;
    margin-left: 8px;
    cursor: pointer;
    vertical-align: middle;

    &:hover :deep(svg) {
      fill: rgba(0, 0, 0, 0.7);
    }
  }
}
</style>
